import { useEffect, useState } from 'react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { ExternalLink, Plus, Users } from 'lucide-react';
import { api, errorMessage } from '@/lib/api/client';
import type {
  Customer,
  OrderListResponse,
  PaginatedResponse,
  TransactionListResponse,
} from '@/lib/api/types';
import { cn } from '@/lib/cn';
import { money, num, percent, phone as phoneFmt, smartDate } from '@/lib/format';
import { useShortcutHint } from '@/app/StatusLine';
import { customerKey } from '@/features/customers/keys';
import { creditLimitText } from '@/features/customers/credit';
import { Badge, Code } from '@/ui/Badge';
import { Button } from '@/ui/Button';
import { DataTable, type Column } from '@/ui/DataTable';
import { SearchInput } from '@/ui/Field';
import { Pagination } from '@/ui/Filters';
import {
  Detail,
  DetailList,
  EmptyState,
  ErrorState,
  Page,
  PageHeader,
  Panel,
  Skeleton,
  Toolbar,
} from '@/ui/Panel';
import { SplitView } from '@/ui/SplitView';
import { balanceLabel, balanceTone } from '@/ui/balance';
import { useDebounced } from '@/ui/useDebounced';
import { asOptionalText, asText } from '@/lib/searchParams';

const PAGE_SIZE = 25;
const SORTS = ['balance', 'name', 'account', 'creditLimit'];

type CustomersSearch = {
  id?: string;
  q: string;
  sort: string;
  order: 'asc' | 'desc';
  page: number;
};

export const Route = createFileRoute('/_app/customers')({
  staticData: { crumb: 'Customers' },
  validateSearch: (s: Record<string, unknown>): CustomersSearch => ({
    id: asOptionalText(s.id),
    q: asText(s.q, ''),
    // Outstanding balances first is the useful default: this list exists to
    // answer "who owes us money", not "who signed up most recently".
    sort: typeof s.sort === 'string' && SORTS.includes(s.sort) ? s.sort : 'balance',
    order: s.order === 'asc' ? 'asc' : 'desc',
    page: Number(s.page) > 0 ? Math.floor(Number(s.page)) : 1,
  }),
  component: CustomersScreen,
});

function CustomersScreen() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const [term, setTerm] = useState(search.q);
  const debounced = useDebounced(term);

  useShortcutHint([
    { keys: 'j / k', label: 'Move' },
    { keys: '⏎', label: 'Inspect' },
  ]);

  // The URL owns the filter; the input owns the keystrokes. They meet here.
  useEffect(() => {
    if (debounced === search.q) return;
    void navigate({
      search: (prev) => ({ ...prev, q: debounced, page: 1 }),
      replace: true,
    });
  }, [debounced, search.q, navigate]);

  const list = useQuery({
    queryKey: ['customers', 'list', search.q, search.sort, search.order, search.page],
    queryFn: () =>
      api.get<PaginatedResponse<Customer>>('/customers', {
        params: {
          search: search.q || undefined,
          sort: search.sort,
          order: search.order,
          page: search.page,
          pageSize: PAGE_SIZE,
        },
      }),
    placeholderData: (prev) => prev,
  });

  const rows = list.data?.data;
  const total = list.data?.total ?? 0;

  const columns: Array<Column<Customer>> = [
    {
      id: 'name',
      header: 'Name',
      sortable: true,
      cell: (row) => (
        <div className="flex min-w-0 items-center gap-1.5">
          <span className="text-ink truncate">{row.name}</span>
          {row.hasCredit ? (
            <span
              className="text-2xs text-ink-faint shrink-0"
              title="House account enabled"
            >
              HA
            </span>
          ) : null}
        </div>
      ),
    },
    {
      id: 'company',
      header: 'Company',
      width: '9rem',
      hideBelow: 'lg',
      cell: (row) => <span className="text-ink-muted">{row.companyName || '—'}</span>,
    },
    {
      id: 'account',
      header: 'Account',
      width: '6rem',
      mono: true,
      sortable: true,
      hideBelow: 'xl',
      cell: (row) => (row.accountNumber ? <Code>{row.accountNumber}</Code> : '—'),
    },
    {
      id: 'phone',
      header: 'Phone',
      width: '7rem',
      mono: true,
      hideBelow: 'md',
      cell: (row) => <span className="text-ink-muted">{phoneFmt(row.phone)}</span>,
    },
    {
      id: 'balance',
      header: 'Balance',
      width: '6rem',
      align: 'right',
      mono: true,
      sortable: true,
      cell: (row) => (
        <span className={balanceTone(row.balance)} title={balanceLabel(row.balance)}>
          {money(row.balance)}
        </span>
      ),
    },
    {
      id: 'creditLimit',
      header: 'Limit',
      width: '5.5rem',
      align: 'right',
      mono: true,
      sortable: true,
      hideBelow: 'md',
      cell: (row) => <span className="text-ink-muted">{creditLimitText(row)}</span>,
    },
    {
      id: 'discount',
      header: 'Disc',
      width: '3.5rem',
      align: 'right',
      mono: true,
      hideBelow: '2xl',
      cell: (row) =>
        row.pctDiscount ? (
          <span className="text-signal-ink">{percent(row.pctDiscount, 0)}</span>
        ) : (
          <span className="text-ink-faint">—</span>
        ),
    },
    {
      id: 'tax',
      header: 'Tax',
      width: '4rem',
      hideBelow: '2xl',
      cell: (row) =>
        row.taxExempt ? (
          <Badge tone="info">Exempt</Badge>
        ) : (
          <span className="text-2xs text-ink-faint">Taxed</span>
        ),
    },
    {
      id: 'active',
      header: 'State',
      width: '4.5rem',
      cell: (row) =>
        row.active === false ? (
          <Badge tone="neutral">Inactive</Badge>
        ) : (
          <Badge tone="positive" dot>
            Active
          </Badge>
        ),
    },
  ];

  return (
    <Page>
      <PageHeader
        eyebrow="People"
        title="Customers"
        subtitle="House accounts, balances and contact details"
        actions={
          <Button
            asChild
            variant="primary"
            size="sm"
            icon={<Plus className="size-3.5" />}
          >
            <Link to="/customers/new">New customer</Link>
          </Button>
        }
      />
      <Toolbar>
        <SearchInput
          size="sm"
          className="w-72"
          value={term}
          onValueChange={setTerm}
          placeholder="Name, company, account number, phone…"
          kbd="/"
        />
        <span className="text-2xs text-ink-faint">
          Sorted by {search.sort === 'creditLimit' ? 'credit limit' : search.sort} ·{' '}
          {search.order === 'desc' ? 'high to low' : 'low to high'}
        </span>
        <div className="ml-auto">
          <Pagination
            page={search.page}
            pageSize={PAGE_SIZE}
            total={total}
            onPageChange={(page) =>
              void navigate({ search: (prev) => ({ ...prev, page }) })
            }
          />
        </div>
      </Toolbar>

      <div className="min-h-0 flex-1">
        <SplitView
          storageKey="customers"
          onCloseDetail={() =>
            void navigate({ search: (prev) => ({ ...prev, id: undefined }) })
          }
          placeholder={
            <EmptyState
              icon={<Users />}
              title="No customer selected"
              description="Pick a row to see contact details, payment setup and recent activity. Open the account for the ledger."
            />
          }
          detail={search.id ? <CustomerInspector id={search.id} /> : undefined}
          list={
            <DataTable
              rows={rows}
              columns={columns}
              rowKey={(row) => row.id}
              loading={list.isLoading}
              error={list.error}
              onRetry={() => void list.refetch()}
              // `Customer.id` is typed as a string but the API sends the raw
              // serial (a number), so both sides are normalised before compare.
              isRowActive={(row) => String(row.id) === search.id}
              rowTone={(row) => (row.balance > 0 ? 'warn' : null)}
              onRowOpen={(row) =>
                void navigate({ search: (prev) => ({ ...prev, id: String(row.id) }) })
              }
              sort={{
                by: search.sort,
                dir: search.order,
                onChange: (by, dir) =>
                  void navigate({
                    search: (prev) => ({ ...prev, sort: by, order: dir, page: 1 }),
                  }),
              }}
              empty={{
                icon: <Users />,
                title: search.q ? 'No customer matches' : 'No customers yet',
                description: search.q
                  ? `Nothing found for “${search.q}”.`
                  : 'Customers appear here once they are created at a register or in the console.',
              }}
              footer={<span>{num(total)} customers</span>}
            />
          }
        />
      </div>
    </Page>
  );
}

/* ── Inspector ───────────────────────────────────────────────────────────────
   Read-only by design: every write on a customer moves through the account
   page, which is where the ledger and the confirmations live.               */

function CustomerInspector({ id }: { id: string }) {
  const customerQuery = useQuery({
    queryKey: customerKey(id),
    queryFn: () => api.get<Customer>(`/customers/${id}`),
  });

  const transactionsQuery = useQuery({
    queryKey: customerKey(id, 'transactions'),
    queryFn: () =>
      api.get<TransactionListResponse>('/transactions', {
        params: { customer_id: id, limit: 8 },
      }),
  });

  const ordersQuery = useQuery({
    queryKey: customerKey(id, 'orders'),
    queryFn: () =>
      api.get<OrderListResponse>('/orders', {
        params: { customerId: id, pageSize: 5 },
      }),
  });

  if (customerQuery.isError) {
    return (
      <ErrorState
        title="Could not load this customer"
        message={errorMessage(customerQuery.error)}
        onRetry={() => void customerQuery.refetch()}
        className="m-3"
      />
    );
  }

  const customer = customerQuery.data;
  if (!customer) {
    return (
      <div className="flex flex-col gap-2 p-3">
        <Skeleton className="h-6 w-40" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  const transactions = transactionsQuery.data?.rows ?? [];
  const orders = ordersQuery.data?.data ?? [];

  return (
    <div className="flex flex-col gap-3 p-3">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <h2 className="text-ink truncate text-base font-semibold">{customer.name}</h2>
          <div className="text-2xs text-ink-faint truncate">
            {customer.companyName || 'No company'}
            {customer.accountNumber ? ` · #${customer.accountNumber}` : ''}
          </div>
        </div>
        <Button asChild size="sm" icon={<ExternalLink className="size-3.5" />}>
          <Link to="/customers/$id" params={{ id }} search={{ page: 1 }}>
            Open account
          </Link>
        </Button>
      </div>

      <Panel title="House account">
        <div className="mb-2 flex items-baseline justify-between gap-3">
          <span
            className={cn(
              'font-mono text-2xl font-medium tracking-tight tabular-nums',
              balanceTone(customer.balance)
            )}
          >
            {money(customer.balance)}
          </span>
          <span className="text-2xs text-ink-faint tracking-wider uppercase">
            {balanceLabel(customer.balance)}
          </span>
        </div>
        <DetailList columns={2}>
          <Detail label="Credit">
            {customer.hasCredit ? (
              <Badge tone="positive">Enabled</Badge>
            ) : (
              <Badge tone="neutral">Off</Badge>
            )}
          </Detail>
          <Detail label="Credit limit" mono>
            {creditLimitText(customer)}
          </Detail>
          <Detail label="Discount" mono>
            {customer.pctDiscount ? percent(customer.pctDiscount, 0) : '—'}
          </Detail>
          <Detail label="Tax">
            {customer.taxExempt ? <Badge tone="info">Exempt</Badge> : 'Taxed'}
          </Detail>
        </DetailList>
      </Panel>

      <Panel title="Contact">
        <DetailList columns={2}>
          <Detail label="Phone" mono>
            {phoneFmt(customer.phone)}
          </Detail>
          <Detail label="Email">{customer.email || '—'}</Detail>
        </DetailList>
      </Panel>

      <Panel title="Payment & security">
        <DetailList columns={2}>
          <Detail label="Account PIN">
            {customer.hasPin ? (
              <Badge tone="positive">Set</Badge>
            ) : (
              <Badge tone="neutral">Not set</Badge>
            )}
          </Detail>
          <Detail label="Card on file" mono>
            {customer.hasCard
              ? `${customer.cardBrand ?? 'Card'} ••••${customer.cardLastFour ?? '????'}`
              : '—'}
          </Detail>
        </DetailList>
      </Panel>

      <Panel title="Recent transactions" description="Last 8" bleed>
        {transactionsQuery.isError ? (
          <div className="text-2xs text-negative p-3">
            {errorMessage(transactionsQuery.error)}
          </div>
        ) : transactionsQuery.isLoading ? (
          <div className="flex flex-col gap-1.5 p-3">
            <Skeleton className="h-3 w-full" />
            <Skeleton className="h-3 w-2/3" />
          </div>
        ) : transactions.length === 0 ? (
          <EmptyState compact title="No sales on this account" />
        ) : (
          <div className="divide-line-soft divide-y">
            {transactions.map((txn) => (
              <div key={txn.id} className="flex items-baseline gap-2 px-3 py-1.5">
                <span className="text-2xs text-ink-faint font-mono tabular-nums">
                  {smartDate(txn.occurredAt)}
                </span>
                <span className="text-ink-muted min-w-0 flex-1 truncate text-xs">
                  {txn.receiptNumber ? `#${txn.receiptNumber}` : txn.id.slice(0, 8)}
                  {txn.status !== 'completed' ? ` · ${txn.status}` : ''}
                </span>
                <span className="text-ink font-mono text-xs tabular-nums">
                  {money(txn.total)}
                </span>
              </div>
            ))}
          </div>
        )}
      </Panel>

      <Panel title="Recent orders" description="Last 5" bleed>
        {ordersQuery.isError ? (
          <div className="text-2xs text-negative p-3">
            {errorMessage(ordersQuery.error)}
          </div>
        ) : ordersQuery.isLoading ? (
          <div className="flex flex-col gap-1.5 p-3">
            <Skeleton className="h-3 w-full" />
            <Skeleton className="h-3 w-2/3" />
          </div>
        ) : orders.length === 0 ? (
          <EmptyState compact title="No orders on this account" />
        ) : (
          <div className="divide-line-soft divide-y">
            {orders.map((order) => (
              <div key={order.id} className="flex items-baseline gap-2 px-3 py-1.5">
                <span className="text-2xs text-ink-faint font-mono tabular-nums">
                  {smartDate(order.createdAt)}
                </span>
                <span className="text-ink-muted min-w-0 flex-1 truncate text-xs">
                  {order.orderNumber} · {order.status}
                </span>
                <span className="text-ink font-mono text-xs tabular-nums">
                  {money(order.actualTotalCents ?? order.estimatedTotalCents)}
                </span>
              </div>
            ))}
          </div>
        )}
      </Panel>

      {customer.notes ? (
        <Panel title="Notes">
          <p className="text-ink-muted text-xs leading-relaxed whitespace-pre-wrap">
            {customer.notes}
          </p>
        </Panel>
      ) : null}
    </div>
  );
}
